import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Home, LayoutDashboard, Image, Users, Newspaper, Star, ShoppingBag, Handshake, FolderOpen, MapPin, Calendar, MessageSquare, Award, Upload, Layers, Building2, Settings, Menu, FileText, Tag, X, Eye, Smartphone } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/mv5wtmf7_Asset%203.png";

const SECTIONS = [
  { key: "sliders", label: "Hero Sliders", icon: Image, fields: [
    { name: "title", label: "Title", type: "text" }, { name: "subtitle", label: "Subtitle", type: "textarea" },
    { name: "image_url", label: "Image", type: "image" }, { name: "cta_text", label: "CTA Button Text", type: "text" },
    { name: "cta_link", label: "CTA Link", type: "text" }, { name: "order", label: "Order", type: "number" },
    { name: "is_active", label: "Active", type: "switch" },
  ]},
  { key: "pages", label: "Content Pages", icon: FolderOpen, fields: [
    { name: "slug", label: "URL Slug", type: "text" }, { name: "title", label: "Title", type: "text" },
    { name: "content", label: "Content", type: "textarea" }, { name: "image_url", label: "Hero Image", type: "image" },
    { name: "banner_image_url", label: "Banner Image (2:1 ratio, shown below title)", type: "image" },
    { name: "collage_images", label: "Collage Images", type: "image_list" },
    { name: "video_url", label: "YouTube Video URL (optional)", type: "text" },
    { name: "is_active", label: "Active", type: "switch" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "impact_numbers", label: "Impact Numbers", icon: Star, fields: [
    { name: "number", label: "Number", type: "text" }, { name: "suffix", label: "Suffix (+, etc.)", type: "text" },
    { name: "label", label: "Label", type: "text" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "president_message", label: "President Message", icon: MessageSquare, fields: [
    { name: "name", label: "Name", type: "text" }, { name: "title", label: "Title/Position", type: "text" },
    { name: "message", label: "Message", type: "textarea" }, { name: "photo_url", label: "Photo", type: "image" },
    { name: "video_url", label: "YouTube Video URL", type: "text" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "alumni", label: "Remarkable Alumni", icon: Award, fields: [
    { name: "name", label: "Name", type: "text" }, { name: "designation", label: "Designation", type: "text" },
    { name: "photo_url", label: "Photo", type: "image" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "testimonials", label: "Testimonials", icon: MessageSquare, fields: [
    { name: "name", label: "Name", type: "text" }, { name: "title", label: "Title/Company", type: "text" },
    { name: "quote", label: "Quote", type: "textarea" }, { name: "photo_url", label: "Photo", type: "image" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "news", label: "News & Press", icon: Newspaper, fields: [
    { name: "title", label: "Title", type: "text" }, { name: "excerpt", label: "Excerpt", type: "textarea" },
    { name: "content", label: "Full Content", type: "textarea" }, { name: "image_url", label: "Image", type: "image" },
    { name: "published_date", label: "Date (YYYY-MM-DD)", type: "text" },
    { name: "is_published", label: "Published", type: "switch" },
    { name: "show_on_home", label: "Show on Homepage", type: "switch" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "areas_of_opportunity", label: "Areas of Opportunity", icon: MapPin, fields: [
    { name: "title", label: "Title", type: "text" }, { name: "description", label: "Description", type: "textarea" },
    { name: "icon", label: "Icon", type: "dropdown", options: ["User", "Heart", "Globe", "Briefcase", "Star", "Award"] },
    { name: "link", label: "Link", type: "text" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "team_members", label: "Team Members", icon: Users, fields: [
    { name: "name", label: "Name", type: "text" }, { name: "designation", label: "Designation", type: "text" },
    { name: "category", label: "Category", type: "dropdown", options: ["governing-board", "local-presidents", "skills-development", "past-presidents"] },
    { name: "bio", label: "Short Bio (shown on hover)", type: "textarea" },
    { name: "photo_url", label: "Photo", type: "image" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "events", label: "Events", icon: Calendar, fields: [
    { name: "title", label: "Title", type: "text" }, { name: "description", label: "Description", type: "textarea" },
    { name: "date", label: "Date", type: "text" }, { name: "location", label: "Location", type: "text" },
    { name: "image_url", label: "Image", type: "image" }, { name: "category", label: "Category", type: "text" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "products", label: "Store Products", icon: ShoppingBag, fields: [
    { name: "name", label: "Product Name", type: "text" }, { name: "description", label: "Description", type: "textarea" },
    { name: "price", label: "Price (BDT)", type: "number" }, { name: "image_url", label: "Image", type: "image" },
    { name: "category", label: "Category", type: "dropdown_dynamic", collection: "product_categories", valueField: "name" },
    { name: "in_stock", label: "In Stock", type: "switch" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "partners", label: "Partners", icon: Handshake, fields: [
    { name: "name", label: "Partner Name", type: "text" }, { name: "logo_url", label: "Logo", type: "image" },
    { name: "website", label: "Website", type: "text" },
    { name: "category", label: "Category", type: "dropdown_dynamic", collection: "partner_categories", valueField: "name" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "media_folders", label: "Media Folders", icon: FolderOpen, fields: [
    { name: "name", label: "Folder Name", type: "text" }, { name: "description", label: "Description", type: "textarea" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "media_gallery", label: "Media Gallery", icon: Image, fields: [
    { name: "title", label: "Title", type: "text" },
    { name: "url", label: "Image", type: "image" },
    { name: "folder_id", label: "Folder", type: "dropdown_dynamic", collection: "media_folders", valueField: "id", labelField: "name" },
    { name: "order", label: "Order", type: "number" },
  ], bulkUpload: true },
  { key: "content_hub", label: "Content Hub", icon: FileText, fields: [
    { name: "title", label: "Title", type: "text" }, { name: "slug", label: "URL Slug", type: "text" },
    { name: "excerpt", label: "Excerpt", type: "textarea" }, { name: "content", label: "Full Content", type: "textarea" },
    { name: "image_url", label: "Image", type: "image" },
    { name: "category", label: "Category", type: "dropdown_dynamic", collection: "content_categories", valueField: "name" },
    { name: "is_published", label: "Published", type: "switch" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "content_categories", label: "Content Categories", icon: Tag, fields: [
    { name: "name", label: "Category Name", type: "text" }, { name: "slug", label: "URL Slug", type: "text" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "partner_categories", label: "Partner Categories", icon: Tag, fields: [
    { name: "name", label: "Category Name", type: "text" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "product_categories", label: "Product Categories", icon: ShoppingBag, fields: [
    { name: "name", label: "Category Name", type: "text" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "homepage_settings", label: "Homepage Settings", icon: Settings, fields: [
    { name: "section_key", label: "Section Key", type: "text", readonly: true },
    { name: "section_title", label: "Section Title", type: "text" },
    { name: "section_subtitle", label: "Section Subtitle", type: "text" },
    { name: "visible", label: "Visible", type: "switch" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "local_organizations", label: "Local Organizations", icon: MapPin, fields: [
    { name: "name", label: "Organization Name", type: "text" }, { name: "city", label: "City", type: "text" },
    { name: "president", label: "President Name", type: "text" }, { name: "contact", label: "Contact", type: "text" },
    { name: "logo_url", label: "Logo", type: "image" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "committees", label: "Committees", icon: Layers, fields: [
    { name: "name", label: "Committee Name", type: "text" }, { name: "slug", label: "URL Slug", type: "text" },
    { name: "image_url", label: "Image", type: "image" }, { name: "description", label: "Short Description", type: "textarea" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "committee_members", label: "Committee Members", icon: Users, fields: [
    { name: "committee_slug", label: "Committee", type: "dropdown_dynamic", collection: "committees", valueField: "slug", labelField: "name" },
    { name: "name", label: "Name", type: "text" }, { name: "designation", label: "Designation", type: "text" },
    { name: "bio", label: "Short Bio", type: "textarea" },
    { name: "photo_url", label: "Photo", type: "image" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "local_org_details", label: "Local Org Details", icon: Building2, fields: [
    { name: "name", label: "Organization Name", type: "text" }, { name: "slug", label: "URL Slug", type: "text" },
    { name: "logo_url", label: "Logo/Card Image (1:1)", type: "image" }, { name: "hero_image_url", label: "Hero Image (1:1 square)", type: "image" },
    { name: "description", label: "Short Description", type: "textarea" },
    { name: "collage_images", label: "Collage Images", type: "image_list" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "social_settings", label: "Social Links", icon: Handshake, fields: [
    { name: "platform", label: "Platform", type: "dropdown", options: ["Facebook", "Linkedin", "Youtube", "Instagram", "Twitter"] },
    { name: "url", label: "URL", type: "text" }, { name: "order", label: "Order", type: "number" },
  ]},
  { key: "menu_items", label: "Menu Manager", icon: Menu, fields: [
    { name: "label", label: "Menu Label", type: "text" },
    { name: "path", label: "URL Path (e.g. /news-press)", type: "text" },
    { name: "parent_id", label: "Parent Menu", type: "dropdown_dynamic", collection: "menu_items", valueField: "id", labelField: "label" },
    { name: "visible", label: "Visible", type: "switch" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "app_links", label: "App Download Links", icon: Smartphone, fields: [
    { name: "key", label: "Platform Key", type: "dropdown", options: ["playstore", "appstore"] },
    { name: "label", label: "Label", type: "text" },
    { name: "url", label: "Store URL", type: "text" },
    { name: "order", label: "Order", type: "number" },
  ]},
  { key: "contact_messages", label: "Contact Messages", icon: MessageSquare, fields: [
    { name: "name", label: "Name", type: "text", readonly: true },
    { name: "email", label: "Email", type: "text", readonly: true },
    { name: "subject", label: "Subject", type: "text", readonly: true },
    { name: "message", label: "Message", type: "textarea", readonly: true },
    { name: "created_at", label: "Submitted At", type: "text", readonly: true },
  ], readonly: true },
];

function ImageUploadButton({ onUploaded, fieldName }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      onUploaded(`${baseUrl}${res.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
      <input type="file" accept="image/*" ref={fileRef} onChange={handleUpload} className="hidden" data-testid={`upload-input-${fieldName}`} />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} className="flex-shrink-0 h-10 px-3" data-testid={`upload-btn-${fieldName}`}>
        <Upload className="w-4 h-4 mr-1" /> {uploading ? "..." : "Upload"}
      </Button>
    </>
  );
}

const IMAGE_DIMENSIONS = {
  image_url: "1920 x 1080px (16:9 landscape)",
  photo_url: "400 x 500px (portrait)",
  logo_url: "400 x 400px (1:1 square)",
  hero_image_url: "400 x 400px (1:1 square)",
  thumbnail_url: "600 x 400px (3:2)",
  banner_image_url: "1920 x 960px (2:1 landscape)",
};

function ImageListField({ images, onChange, fieldName }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Normalize: handle legacy comma-separated strings
  const imgList = Array.isArray(images) ? images : (typeof images === "string" && images.trim() ? images.split(",").map(s => s.trim()).filter(Boolean) : []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      onChange([...imgList, `${baseUrl}${res.data.url}`]);
      toast.success("Image added");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => onChange(imgList.filter((_, i) => i !== idx));

  return (
    <div className="mt-1.5" data-testid={`form-${fieldName}`}>
      <div className="flex flex-wrap gap-3 mb-3">
        {imgList.map((url, i) => (
          <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border bg-slate-50">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-img-${fieldName}-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <input type="file" accept="image/*" ref={fileRef} onChange={handleUpload} className="hidden" />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`add-img-${fieldName}`}>
        <Plus className="w-4 h-4 mr-1" /> {uploading ? "Uploading..." : "Add Image"}
      </Button>
    </div>
  );
}

function BulkUploadButton({ folderId, onDone }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleBulkUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      const res = await api.post("/upload/bulk", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const uploaded = res.data.uploaded.filter(u => !u.error);
      // Create media_gallery entries for each uploaded file
      for (const u of uploaded) {
        const fullUrl = `${baseUrl}${u.url}`;
        await api.post("/content/media_gallery", {
          title: u.filename,
          type: "image",
          url: fullUrl,
          thumbnail_url: fullUrl,
          folder_id: folderId || "",
          order: 0,
        });
      }
      toast.success(`${uploaded.length} images uploaded`);
      onDone();
    } catch (err) {
      toast.error("Bulk upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <>
      <input type="file" accept="image/*" multiple ref={fileRef} onChange={handleBulkUpload} className="hidden" data-testid="bulk-upload-input" />
      <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="bulk-upload-btn" className="border-jci-teal text-jci-teal hover:bg-jci-teal hover:text-white">
        <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading..." : "Bulk Upload Images"}
      </Button>
    </>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("sliders");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dynamicOptions, setDynamicOptions] = useState({});

  const section = SECTIONS.find(s => s.key === activeSection);
  usePageTitle(`Admin - ${section?.label || "Dashboard"}`);

  // Load dynamic dropdown options
  useEffect(() => {
    const dynamicFields = section?.fields.filter(f => f.type === "dropdown_dynamic") || [];
    dynamicFields.forEach(f => {
      api.get(`/content/${f.collection}`).then(res => {
        setDynamicOptions(prev => ({ ...prev, [f.collection]: res.data }));
      }).catch(() => {});
    });
  }, [section]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/content/${activeSection}`);
      setItems(res.data);
    } catch { toast.error("Failed to load items"); }
    setLoading(false);
  }, [activeSection]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const openAdd = () => {
    const defaults = {};
    section.fields.forEach(f => {
      if (f.readonly) return;
      defaults[f.name] = f.type === "switch" ? true : f.type === "number" ? 0 : f.type === "image_list" ? [] : "";
    });
    setFormData(defaults);
    setEditItem(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setFormData({ ...item });
    setEditItem(item);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await api.put(`/content/${activeSection}/${editItem.id}`, formData);
        toast.success("Updated successfully");
      } else {
        await api.post(`/content/${activeSection}`, formData);
        toast.success("Created successfully");
      }
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await api.delete(`/content/${activeSection}/${id}`);
      toast.success("Deleted");
      loadItems();
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = () => { logout(); navigate("/admin"); };

  return (
    <div className="flex h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-jci-black flex flex-col transition-all duration-300 flex-shrink-0`}>
        <div className="p-4 border-b border-white/5">
          {sidebarOpen ? (
            <img src={LOGO} alt="JCI" className="h-8" />
          ) : (
            <div className="w-8 h-8 bg-jci-blue rounded-lg flex items-center justify-center text-white text-xs font-bold">JCI</div>
          )}
        </div>
        <ScrollArea className="flex-1 py-2">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              data-testid={`sidebar-${s.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                activeSection === s.key ? 'bg-jci-blue/20 text-jci-blue' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
            >
              <s.icon className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{s.label}</span>}
            </button>
          ))}
        </ScrollArea>
        <div className="p-3 border-t border-white/5 space-y-2">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white/40 hover:text-white/70 transition-colors" data-testid="admin-view-site">
            <Home className="w-4 h-4" />{sidebarOpen && "View Site"}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-400/60 hover:text-red-400 transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" />{sidebarOpen && "Logout"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-slate-700" data-testid="toggle-sidebar">
              <LayoutDashboard className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-jci-black">{section?.label}</h1>
              <p className="text-xs text-slate-400">{items.length} items</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{admin?.name || admin?.email}</span>
            {section?.bulkUpload && (
              <BulkUploadButton folderId="" onDone={loadItems} />
            )}
            {!section?.readonly && (
              <Button onClick={openAdd} data-testid="add-item-btn" className="bg-jci-blue hover:bg-jci-navy text-white">
                <Plus className="w-4 h-4 mr-2" /> Add New
              </Button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-jci-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              {section && <section.icon className="w-12 h-12 text-slate-200 mx-auto mb-4" />}
              <p className="text-slate-400">No items yet. Click "Add New" to create one.</p>
            </div>
          ) : (
            <div className="grid gap-3" data-testid="items-list">
              {items.map((item, i) => (
                <Card key={item.id} className="border shadow-sm hover:shadow-md transition-shadow" data-testid={`item-row-${i}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {(item.image_url || item.photo_url || item.logo_url || item.url) && (
                        <img src={item.image_url || item.photo_url || item.logo_url || item.url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-jci-black text-sm truncate">
                          {item.title || item.name || item.label || item.slug || item.section_key || item.subject || item.platform || `Item ${i + 1}`}
                        </p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {item.subtitle || item.designation || item.excerpt || item.category || item.email || item.subject || item.section_title || item.path || item.description?.substring(0, 60) || item.quote?.substring(0, 60) || ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.visible === false && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
                      {(item.is_active === false || item.is_published === false) && <Badge variant="secondary" className="text-xs">Draft</Badge>}
                      {item.show_on_home && <Badge className="text-xs bg-jci-teal/20 text-jci-teal border-0">Home</Badge>}
                      {item.order !== undefined && <Badge variant="outline" className="text-xs">#{item.order}</Badge>}
                      {!section?.readonly && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-item-${i}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} data-testid={`delete-item-${i}`} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      {section?.readonly && section.fields.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`view-item-${i}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{section?.readonly ? "View" : editItem ? "Edit" : "Add"} {section?.label?.replace(/s$/, '')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {section?.fields.map(f => {
              if (f.readonly && editItem) {
                return (
                  <div key={f.name}>
                    <Label className="text-sm font-medium">{f.label}</Label>
                    {f.type === "textarea" ? (
                      <p className="text-sm text-slate-500 mt-1 bg-slate-50 px-3 py-2 rounded-md whitespace-pre-wrap">{formData[f.name] || "—"}</p>
                    ) : (
                      <p className="text-sm text-slate-500 mt-1 bg-slate-50 px-3 py-2 rounded-md">{formData[f.name] || "—"}</p>
                    )}
                  </div>
                );
              }
              if (f.readonly && !editItem) return null;

              const isImageField = f.type === "image";
              const dimHint = IMAGE_DIMENSIONS[f.name];

              return (
                <div key={f.name}>
                  <Label className="text-sm font-medium">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea data-testid={`form-${f.name}`} value={formData[f.name] || ""} onChange={e => setFormData({...formData, [f.name]: e.target.value})} rows={3} className="mt-1.5" />
                  ) : f.type === "switch" ? (
                    <div className="mt-1.5">
                      <Switch data-testid={`form-${f.name}`} checked={!!formData[f.name]} onCheckedChange={v => setFormData({...formData, [f.name]: v})} />
                    </div>
                  ) : f.type === "number" ? (
                    <Input data-testid={`form-${f.name}`} type="number" value={formData[f.name] || 0} onChange={e => setFormData({...formData, [f.name]: parseFloat(e.target.value) || 0})} className="mt-1.5" />
                  ) : f.type === "dropdown" ? (
                    <Select value={formData[f.name] || ""} onValueChange={v => setFormData({...formData, [f.name]: v})}>
                      <SelectTrigger className="mt-1.5" data-testid={`form-${f.name}`}>
                        <SelectValue placeholder={`Select ${f.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options?.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "dropdown_dynamic" ? (
                    <Select value={formData[f.name] || ""} onValueChange={v => setFormData({...formData, [f.name]: v})}>
                      <SelectTrigger className="mt-1.5" data-testid={`form-${f.name}`}>
                        <SelectValue placeholder={`Select ${f.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {(dynamicOptions[f.collection] || []).map(opt => (
                          <SelectItem key={opt.id} value={opt[f.valueField]}>{opt[f.labelField || f.valueField]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : isImageField ? (
                    <div>
                      <div className="flex gap-2 mt-1.5">
                        <Input data-testid={`form-${f.name}`} value={formData[f.name] || ""} onChange={e => setFormData({...formData, [f.name]: e.target.value})} className="flex-1" placeholder="Enter URL or upload image" />
                        <ImageUploadButton onUploaded={(url) => setFormData({...formData, [f.name]: url})} fieldName={f.name} />
                      </div>
                      {dimHint && <p className="text-[10px] text-slate-400 mt-1">Recommended: {dimHint}</p>}
                      {formData[f.name] && (
                        <div className="mt-2 relative group w-20 h-20 rounded-lg overflow-hidden border bg-slate-50">
                          <img src={formData[f.name]} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  ) : f.type === "image_list" ? (
                    <ImageListField
                      images={formData[f.name] || []}
                      onChange={(imgs) => setFormData({...formData, [f.name]: imgs})}
                      fieldName={f.name}
                    />
                  ) : (
                    <Input data-testid={`form-${f.name}`} value={formData[f.name] || ""} onChange={e => setFormData({...formData, [f.name]: e.target.value})} className="mt-1.5" />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{section?.readonly ? "Close" : "Cancel"}</Button>
            {!section?.readonly && (
              <Button onClick={handleSave} data-testid="save-item-btn" className="bg-jci-blue hover:bg-jci-navy text-white">
                {editItem ? "Update" : "Create"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
